import React, { useContext, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { postRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import AuthContext from '../../AuthContext';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';
import { filterCrmWorkflowStatesByPhase } from '../../../utils/crmCommunicationTypeAliases';
import {
  canSetCrmInspectorState,
  canUserAssignCrmInspector,
  isCrmInspectorReassignLocked,
} from '../../../utils/crmCommunicationAssigneeAccess';
import { CrmPanelShell } from './panelLayout';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';
import { crmInspectionSectionLabelKey } from '../../../utils/crmCommunicationLevelLabels';
import {
  canUserCloseCrmTicketFromManualPhase,
  crmOutcomePhaseForTicketType,
  findCrmClosedStateId,
  ticketStatusMatchesManualClosure,
} from '../../../utils/crmCommunicationClosure';

function stateLabel(row) {
  const n = row?.name;
  if (n && typeof n === 'object') return n.sk || n.en || n.de || '';
  return n || '';
}

/**
 * Výsledok akvizície / dosledovanie ponuky (parita FP, CRM terminológia).
 * @param {{ ticketId: number, ticket?: Record<string, unknown>|null, ticketType: string, inspection?: Array<Record<string, unknown>>, onChanged: () => void, readOnly?: boolean, embedded?: boolean }} props
 */
export default function CrmCommunicationInspectionPanel({
  ticketId,
  ticket = null,
  ticketType,
  inspection,
  onChanged,
  onCloseTicket = null,
  readOnly = false,
  workflowLocked = false,
  approvalInProgress = false,
  embedded = false,
}) {
  const [state] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [busy, setBusy] = useState(false);

  const inspectionLabelKey = crmInspectionSectionLabelKey(ticketType);
  const panelTitle = t(inspectionLabelKey);
  const levelSectionLabel = panelTitle;

  const allUsers = Array.isArray(state?.users) ? state.users : [];
  const levels = useMemo(
    () => (Array.isArray(inspection) ? inspection.slice().sort((a, b) => Number(a?.level ?? 0) - Number(b?.level ?? 0)) : []),
    [inspection],
  );

  const inspectorStates = useMemo(() => {
    const all = Array.isArray(state?.crm_states) ? state.crm_states : [];
    const outcomePhase = ticketType === 'quote_request' ? 'followup' : 'outcome';
    return filterCrmWorkflowStatesByPhase(all, outcomePhase, ticketType);
  }, [state?.crm_states, ticketType]);

  const hasAdminOverride = useMemo(() => {
    if (user?.is_superuser) return true;
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    return roles.some((r) => (typeof r === 'string' ? r.toLowerCase() === 'admin' : String(r?.name || '').toLowerCase() === 'admin'));
  }, [user?.is_superuser, user?.roles]);

  const levelLabel = (row) => {
    const fromLevel = row?.level_def?.name;
    if (fromLevel && typeof fromLevel === 'string') return fromLevel;
    return `${t('CrmCommunication.Detail.Level')} ${row?.level ?? ''}`;
  };

  const workflowBlocksInspectorPick = useMemo(() => {
    const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
    const crmStates = Array.isArray(state?.crm_states) ? state.crm_states : [];
    const status = crmStates.find((s) => Number(s.id) === statusId);
    if (!status) return false;
    const phase = String(status.phase || '');
    return ['created', 'system_approval'].includes(phase);
  }, [state?.crm_states, ticket?.status_crm_state_id, ticket?.status_id]);

  const closedStateId = useMemo(
    () => findCrmClosedStateId(state?.crm_states),
    [state?.crm_states],
  );

  const outcomePhase = crmOutcomePhaseForTicketType(ticketType);

  const showCloseButton = useMemo(
    () => ticketStatusMatchesManualClosure(state?.crm_states, ticket, outcomePhase, ticketType),
    [state?.crm_states, ticket, ticketType, outcomePhase],
  );

  const canCloseTicket = useMemo(
    () => canUserCloseCrmTicketFromManualPhase(ticket, user, state, outcomePhase, ticketType),
    [ticket, user, state, ticketType, outcomePhase],
  );

  const ticketWorkflowClosed = useMemo(() => {
    const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
    const crmStates = Array.isArray(state?.crm_states) ? state.crm_states : [];
    const status = crmStates.find((s) => Number(s.id) === statusId);
    if (!status) return false;
    const phase = String(status.phase || '');
    const tt = String(status.transition_type || '');
    return phase === 'closing' && ['final_auto', 'final_manually'].includes(tt);
  }, [state?.crm_states, ticket?.status_crm_state_id, ticket?.status_id]);

  const setInspector = async (row, inspectorId) => {
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_inspector`, { level_id: row.level_id, inspector_id: inspectorId }, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.AssigneeSaved');
          return res;
        } else {
          throw new Error(String(res?.message || 'Error'));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const setInspectorState = async (row, inspectorStateId) => {
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_inspector_state`, { level_id: row.level_id, inspector_state: inspectorStateId }, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
          return res;
        } else {
          throw new Error(String(res?.message || 'Error'));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmPanelShell
      embedded={embedded}
      title={panelTitle}
      t={t}
    >
      <ul className={`${embedded ? 'mt-1' : 'mt-3'} space-y-3`}>
        {levels.length === 0 ? <li>{t('CrmCommunication.Detail.NoData')}</li> : null}
        {levels.map((row, idx) => {
          const allowedInspectors = Array.isArray(row?.possible_inspectors) ? row.possible_inspectors.map((x) => Number(x)) : [];
          const inspectorOptions = allUsers
            .filter((u) => allowedInspectors.includes(Number(u.id)))
            .map((u) => ({ label: u?.employee?.name || u?.username || `#${u.id}`, value: u.id }));
          const stateOptions = inspectorStates.map((s) => ({ label: stateLabel(s), value: s.id }));
          const canPickInspector = !readOnly && !busy && !workflowBlocksInspectorPick
            && !isCrmInspectorReassignLocked(row)
            && (hasAdminOverride || canUserAssignCrmInspector(
              ticket,
              user,
              state,
              row,
              workflowLocked,
              approvalInProgress,
            ));
          const canEditInspectorState = !readOnly && !busy && !ticketWorkflowClosed
            && canSetCrmInspectorState(ticket, user, row, state?.crm_states ?? [], workflowLocked);
          const inspectorStateDisabled = !canEditInspectorState;
          const inspectorStateValue = Number(row?.inspector_state ?? 0) > 0
            ? Number(row.inspector_state)
            : null;

          return (
            <li key={String(row.level_id || idx)} className="flex flex-col mt-2 mb-5">
              <label className="font-bold mb-1">
                {levelSectionLabel}{levelLabel(row) ? ` – ${levelLabel(row)}` : ''}
              </label>
              <Dropdown
                value={row?.inspector_id != null ? Number(row.inspector_id) : null}
                options={inspectorOptions}
                onChange={(e) => void setInspector(row, e.value)}
                disabled={!canPickInspector}
                optionLabel="label"
                optionValue="value"
                filter
                placeholder={t('ServiceTaskDetail.SelectEmployee')}
                className="w-60 px-2 py-0 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              />
              {row?.inspector_id ? (
                <>
                  <label className="font-normal text-md mb-1 mt-2">{t('ServiceTask.StatusLabel')}</label>
                  <Dropdown
                    value={inspectorStateValue}
                    options={stateOptions}
                    onChange={(e) => void setInspectorState(row, e.value)}
                    disabled={inspectorStateDisabled}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t('Common.SelectOption')}
                    className="w-60 px-2 py-0 mt-1 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                  {showCloseButton ? (
                    <button
                      type="button"
                      disabled={!canCloseTicket || busy || !closedStateId || typeof onCloseTicket !== 'function'}
                      onClick={() => onCloseTicket(closedStateId)}
                      className="flex justify-center items-center mt-4 w-60 h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
                    >
                      {t('ServiceTaskDetail.Close')}
                    </button>
                  ) : null}
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    </CrmPanelShell>
  );
}
